import React, { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { StrategyMap, StrategyMapPerspective } from '../../../models'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { updateWorkspaceAsync, updateAnalysisReport } from '../../../state/slices/conversationsSlice'
import { omapApi } from '../../../services/omapApi'
import { StrategyMapRenderer } from '../visualizations/StrategyMapRenderer'
import { navigateToMeceTactic, WorkspaceSubTab } from '../../../utils/crossTabNavigation'
import { useTabNavigation } from '../../../contexts/TabNavigationContext'
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '../../../state/hooks/useIsSampleCompany'
import './StrategyMapEditorSimple.css'

interface StrategyMapEditorSimpleProps {
  data: StrategyMap
  conversationId: string
  onSaveSuccess?: () => void
  onNodeClick?: (nodeId: string) => void
  // Phase 2.3: Stale warning props
  isStale?: boolean
  staleReason?: string
  onRegenerateRequest?: () => void
  // Jan 2026: For MECE navigation
  setWorkspaceSubTab?: (subTab: WorkspaceSubTab) => void
  // Phase 4: Execution health overlay
  executionHealth?: Record<string, 'on_track' | 'at_risk' | 'off_track'>
}

interface ObjectiveFormData {
  objective_id: string
  objective_name: string
  description: string
}

interface CausalLink {
  source_objective_id: string
  target_objective_id: string
  relationship: string
}

const BSC_DISPLAY_ORDER = ['Financial', 'Customer', 'Internal Process', 'Learning & Growth']

// Convert pillars format to perspectives format for backwards compatibility
function convertPillarsToPerspectives(data: StrategyMap): StrategyMapPerspective[] {
  // If perspectives already exist, use them
  if (data.perspectives && data.perspectives.length > 0) {
    return data.perspectives
  }

  // If pillars exist, convert them to perspectives format
  const pillars = (data as any).pillars
  if (pillars && pillars.length > 0) {
    // Group by BSC perspective (Financial, Customer, Internal Process, Learning & Growth)
    const perspectiveGroups: Record<string, any[]> = {
      'Financial': [],
      'Customer': [],
      'Internal Process': [],
      'Learning & Growth': []
    }

    pillars.forEach((pillar: any, index: number) => {
      // Create objectives from tactics in each pillar
      const objectives = (pillar.tactics || []).map((tactic: any, tacIdx: number) => ({
        objective_id: tactic.initiative_id || `obj_${index}_${tacIdx}`,
        objective_name: tactic.title || pillar.name,
        description: tactic.description || pillar.description || ''
      }))

      // If no tactics, create one objective from the pillar itself.
      // IMPORTANT: use the real pillar.objective_id (e.g., "F1", "C1") so
      // handleSave can map draft edits back to the persisted pillar by ID.
      if (objectives.length === 0) {
        objectives.push({
          objective_id: pillar.objective_id || `obj_pillar_${index}`,
          objective_name: pillar.name,
          description: pillar.description || ''
        })
      }

      // Assign to a perspective based on pillar characteristics or default to Internal Process
      const perspective = detectPerspective(pillar)
      perspectiveGroups[perspective].push(...objectives)
    })

    // Convert groups to perspective array in canonical BSC display order
    return BSC_DISPLAY_ORDER
      .filter(name => perspectiveGroups[name].length > 0)
      .map((name, index) => ({
        perspective_id: `p${index + 1}`,
        perspective_name: name,
        objectives: perspectiveGroups[name]
      }))
  }

  // If objectives exist (new format), convert them
  if (data.objectives && data.objectives.length > 0) {
    const perspectiveGroups: Record<string, any[]> = {}

    data.objectives.forEach((obj) => {
      const perspective = obj.perspective || 'Internal Process'
      if (!perspectiveGroups[perspective]) {
        perspectiveGroups[perspective] = []
      }
      perspectiveGroups[perspective].push({
        objective_id: obj.objective_id,
        objective_name: obj.strategic_intent || obj.description,
        description: obj.description
      })
    })

    // Sort by canonical BSC display order, with any unknown perspectives appended
    const knownNames = BSC_DISPLAY_ORDER.filter(name => perspectiveGroups[name] && perspectiveGroups[name].length > 0)
    const unknownNames = Object.keys(perspectiveGroups).filter(name => !BSC_DISPLAY_ORDER.includes(name) && perspectiveGroups[name].length > 0)
    return [...knownNames, ...unknownNames]
      .map((name, index) => ({
        perspective_id: `p${index + 1}`,
        perspective_name: name,
        objectives: perspectiveGroups[name]
      }))
  }

  return []
}

// Detect BSC perspective from pillar name/content
function detectPerspective(pillar: any): string {
  const name = (pillar.name || '').toLowerCase()
  const description = (pillar.description || '').toLowerCase()
  const combined = name + ' ' + description

  if (combined.includes('financial') || combined.includes('revenue') || combined.includes('profit') || combined.includes('cost')) {
    return 'Financial'
  }
  if (combined.includes('customer') || combined.includes('client') || combined.includes('satisfaction') || combined.includes('market')) {
    return 'Customer'
  }
  if (combined.includes('learning') || combined.includes('growth') || combined.includes('talent') || combined.includes('training') || combined.includes('capability')) {
    return 'Learning & Growth'
  }
  // Default to Internal Process
  return 'Internal Process'
}

export const StrategyMapEditorSimple: React.FC<StrategyMapEditorSimpleProps> = ({
  data,
  conversationId,
  onSaveSuccess,
  onNodeClick,
  isStale = false,
  staleReason,
  onRegenerateRequest,
  setWorkspaceSubTab,
  executionHealth,
}) => {
  // Convert data to perspectives format (handles both legacy and new formats)
  const normalizedPerspectives = useMemo(() => convertPillarsToPerspectives(data), [data])

  const [isEditMode, setIsEditMode] = useState(false)
  const isSampleCompany = useIsSampleCompany()
  const [draftPerspectives, setDraftPerspectives] = useState<StrategyMapPerspective[]>(normalizedPerspectives)
  const [draftCausalLinks, setDraftCausalLinks] = useState<CausalLink[]>(data.causal_links || [])
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [showCoherencePanel, setShowCoherencePanel] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'failed'>('idle')

  const dispatch = useAppDispatch()
  const activeConversation = useAppSelector(state =>
    state.conversations.conversations.find(c => c.id === conversationId)
  )
  const { navigateToTab } = useTabNavigation()

  // Handler to navigate to MECE tactic (Jan 2026)
  const handleNavigateToMeceTactic = (tacticRef: string) => {
    navigateToMeceTactic(tacticRef, navigateToTab, setWorkspaceSubTab)
  }

  // Update draft when data changes
  useEffect(() => {
    if (!isEditMode) {
      setDraftPerspectives(normalizedPerspectives)
      setDraftCausalLinks(data.causal_links || [])
    }
  }, [normalizedPerspectives, isEditMode, data.causal_links])

  const handleEditToggle = () => {
    if (isEditMode && isDirty) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?')
      if (!confirmCancel) return
    }

    if (!isEditMode) {
      setIsEditMode(true)
      setDraftPerspectives(normalizedPerspectives)
      setDraftCausalLinks(data.causal_links || [])
      setIsDirty(false)
      setValidationErrors([])
    } else {
      setIsEditMode(false)
      setDraftPerspectives(normalizedPerspectives)
      setDraftCausalLinks(data.causal_links || [])
      setIsDirty(false)
      setValidationErrors([])
    }
  }

  const handleSave = async () => {
    // Client-side validation
    const errors: string[] = []

    draftPerspectives.forEach(perspective => {
      if (!perspective.objectives || perspective.objectives.length === 0) {
        errors.push(`Perspective "${perspective.perspective_name}" must have at least one objective`)
      }

      perspective.objectives.forEach((obj, idx) => {
        if (!obj.objective_name || obj.objective_name.trim().length < 5) {
          errors.push(`Perspective "${perspective.perspective_name}", Objective #${idx + 1}: Name is too short (min 5 characters)`)
        }
        if (!obj.description || obj.description.trim().length < 10) {
          errors.push(`Perspective "${perspective.perspective_name}", Objective "${obj.objective_name}": Description is too short (min 10 characters)`)
        }
      })
    })

    // Causal-link validation
    const seenPairs = new Set<string>()
    draftCausalLinks.forEach((link, idx) => {
      const num = idx + 1
      if (!link.source_objective_id || !link.target_objective_id) {
        errors.push(`Causal link #${num}: source and target required`)
      } else if (link.source_objective_id === link.target_objective_id) {
        errors.push(`Causal link #${num}: source and target must be different (self-loop not allowed)`)
      } else {
        const pairKey = `${link.source_objective_id}→${link.target_objective_id}`
        if (seenPairs.has(pairKey)) {
          errors.push(`Causal link #${num}: duplicate (source, target) pair`)
        } else {
          seenPairs.add(pairKey)
        }
      }
      if (!link.relationship || link.relationship.trim().length === 0) {
        errors.push(`Causal link #${num}: relationship is required`)
      }
    })

    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsSaving(true)
    setValidationErrors([])

    try {
      // Source-of-truth fix (Phase 2A-bis): the GET endpoint at
      // backend main.py:5070-5108 rebuilds perspectives[] from pillars[]
      // on every read. So edits to perspectives[] don't survive a
      // round-trip — pillars[] is the persistent source of truth.
      // Build an updated pillars[] by matching draft objective_id back
      // to the original pillar.
      const draftObjectivesById = new Map<string, ObjectiveFormData>()
      draftPerspectives.forEach(p => {
        p.objectives.forEach(obj => {
          if (obj.objective_id) {
            draftObjectivesById.set(obj.objective_id, obj as ObjectiveFormData)
          }
        })
      })

      const originalPillars = (data as any).pillars || []
      const updatedPillars = originalPillars.map((pillar: any) => {
        const matched = draftObjectivesById.get(pillar.objective_id)
        if (!matched) return pillar
        return {
          ...pillar,
          name: matched.objective_name,
          description: matched.description,
        }
      })

      // Derive canonical objectives[] from updatedPillars so full-mode
      // validation passes. The GET endpoint at backend main.py:5097-5105
      // returns a REDUCED objectives projection that lacks
      // strategic_intent / outcome_type / perspective; if we re-submit
      // that, the validator rejects every objective. Build the canonical
      // shape locally — same defaulting rules the migration uses.
      const updatedObjectives = updatedPillars.map((pillar: any) => {
        const description = (pillar.description || '').trim()
        const name = (pillar.name || '').trim()
        const strategic_intent =
          description.length >= 10 ? description :
          name || 'Strategic objective'
        return {
          objective_id: pillar.objective_id,
          objective_name: pillar.name,
          description: pillar.description,
          strategic_intent,
          outcome_type: pillar.outcome_type || 'Lagging',
          perspective: pillar.bsc_perspective || 'Internal Process',
        }
      })

      const updatedStrategyMap = {
        ...data,
        pillars: updatedPillars,
        objectives: updatedObjectives,
        perspectives: draftPerspectives,
        causal_links: draftCausalLinks,
      }

      const result = await dispatch(updateWorkspaceAsync({
        conversationId,
        updates: { strategy_map: updatedStrategyMap }
      })).unwrap()

      setIsEditMode(false)
      setIsDirty(false)
      setIsSaving(false)
      onSaveSuccess?.()

      // Auto-sync Analysis Report (non-blocking)
      const fullReport = activeConversation?.analysisReportData?.fullReport
      if (fullReport) {
        setSyncStatus('syncing')
        omapApi.syncStrategyReport(
          conversationId,
          fullReport,
          result.updatedWorkspace || { strategy_map: updatedStrategyMap },
          'strategy_map'
        ).then(syncResult => {
          dispatch(updateAnalysisReport({
            conversationId,
            data: { fullReport: syncResult.document }
          }))
          setSyncStatus('synced')
          setTimeout(() => setSyncStatus('idle'), 2000)
        }).catch(err => {
          console.warn('[StrategyMapEditor] Report sync failed (save succeeded):', err)
          setSyncStatus('failed')
          setTimeout(() => setSyncStatus('idle'), 3000)
        })
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save Strategy Map changes'
      setValidationErrors([errorMessage])
      setIsSaving(false)
    }
  }

  const handleAddObjective = (perspectiveId: string) => {
    const newObjective: ObjectiveFormData = {
      objective_id: `obj_${Date.now()}`,
      objective_name: 'New Objective',
      description: 'Describe this strategic objective.'
    }

    const updatedPerspectives = draftPerspectives.map(p => {
      if (p.perspective_id === perspectiveId) {
        return {
          ...p,
          objectives: [...(p.objectives || []), newObjective]
        }
      }
      return p
    })

    setDraftPerspectives(updatedPerspectives)
    setIsDirty(true)
  }

  const handleUpdateObjective = (perspectiveId: string, objectiveId: string, field: keyof ObjectiveFormData, value: string) => {
    const updatedPerspectives = draftPerspectives.map(p => {
      if (p.perspective_id === perspectiveId) {
        return {
          ...p,
          objectives: p.objectives.map(obj =>
            obj.objective_id === objectiveId
              ? { ...obj, [field]: value }
              : obj
          )
        }
      }
      return p
    })

    setDraftPerspectives(updatedPerspectives)
    setIsDirty(true)
  }

  const handleDeleteObjective = (perspectiveId: string, objectiveId: string) => {
    const confirmed = window.confirm('Delete this objective?')
    if (!confirmed) return

    const updatedPerspectives = draftPerspectives.map(p => {
      if (p.perspective_id === perspectiveId) {
        return {
          ...p,
          objectives: p.objectives.filter(obj => obj.objective_id !== objectiveId)
        }
      }
      return p
    })

    setDraftPerspectives(updatedPerspectives)

    // Auto-prune causal links that reference the deleted objective
    const prunedLinks = draftCausalLinks.filter(
      link => link.source_objective_id !== objectiveId && link.target_objective_id !== objectiveId
    )
    const pruneCount = draftCausalLinks.length - prunedLinks.length
    setDraftCausalLinks(prunedLinks)
    if (pruneCount > 0) {
      toast(`Pruned ${pruneCount} causal link${pruneCount === 1 ? '' : 's'} pointing to this objective.`)
    }

    setIsDirty(true)
  }

  const handleAddCausalLink = () => {
    setDraftCausalLinks([
      ...draftCausalLinks,
      { source_objective_id: '', target_objective_id: '', relationship: '' }
    ])
    setIsDirty(true)
  }

  const handleUpdateCausalLink = (
    idx: number,
    field: 'source_objective_id' | 'target_objective_id' | 'relationship',
    value: string
  ) => {
    setDraftCausalLinks(draftCausalLinks.map((link, i) =>
      i === idx ? { ...link, [field]: value } : link
    ))
    setIsDirty(true)
  }

  const handleDeleteCausalLink = (idx: number) => {
    setDraftCausalLinks(draftCausalLinks.filter((_, i) => i !== idx))
    setIsDirty(true)
  }

  if (normalizedPerspectives.length === 0) {
    return (
      <div className="strategy-map-editor-simple">
        <div className="no-data-message">
          No strategy map data available.
        </div>
      </div>
    )
  }

  if (!isEditMode) {
    return (
      <div className="strategy-map-editor-simple">
        {/* Phase 2.3: Stale warning banner */}
        {isStale && (
          <div className="stale-warning-banner">
            <span className="warning-icon">⚠️</span>
            <span className="warning-text">
              Strategy Map may be outdated: {staleReason || 'MECE table was modified'}
            </span>
            {onRegenerateRequest && (
              <button
                className="btn-regenerate"
                onClick={onRegenerateRequest}
                disabled={isSampleCompany}
                title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
              >
                Regenerate Strategy Map
              </button>
            )}
          </div>
        )}

        <div className="editor-header">
          <button
            className="btn-edit"
            onClick={handleEditToggle}
            disabled={isSampleCompany}
            title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
          >
            Edit Strategy Map
          </button>
          {/* Jan 2026: Toggle coherence details panel */}
          {data.coherence_status && (
            <button
              className={`btn-coherence-toggle ${showCoherencePanel ? 'active' : ''}`}
              onClick={() => setShowCoherencePanel(!showCoherencePanel)}
            >
              {showCoherencePanel ? 'Hide' : 'Show'} Coherence Details
            </button>
          )}
        </div>

        {/* Jan 2026: Coherence Status Panel */}
        {showCoherencePanel && data.coherence_status && (
          <div className="coherence-status-panel">
            <h4>Strategic Coherence</h4>

            {/* Pass/Fail Indicators */}
            <div className="status-indicators">
              <span className={`status-badge ${data.coherence_status.traceability_passed ? 'pass' : 'fail'}`}>
                Traceability: {data.coherence_status.traceability_passed ? '✓ Pass' : '✗ Fail'}
              </span>
              <span className={`status-badge ${data.coherence_status.completeness_passed ? 'pass' : 'fail'}`}>
                Completeness: {data.coherence_status.completeness_passed ? '✓ Pass' : '✗ Fail'}
              </span>
            </div>

            {/* Metrics Display */}
            {data.coherence_metrics && (
              <div className="coherence-metrics">
                <div className="metric-row">
                  <span className="metric-label">Overall Coherence:</span>
                  <span className={`metric-value ${data.coherence_metrics.overall_coherence_score >= 90 ? 'good' : data.coherence_metrics.overall_coherence_score >= 70 ? 'warning' : 'poor'}`}>
                    {data.coherence_metrics.overall_coherence_score}%
                  </span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Initiative Coverage:</span>
                  <span className="metric-value">
                    {data.coherence_metrics.tactic_coverage_pct}% ({data.coherence_metrics.referenced_tactics}/{data.coherence_metrics.total_mece_tactics})
                  </span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Initiative Grounding:</span>
                  <span className="metric-value">
                    {data.coherence_metrics.initiative_grounding_pct}% ({data.coherence_metrics.grounded_initiatives}/{data.coherence_metrics.mece_origin_initiatives})
                  </span>
                </div>
              </div>
            )}

            {/* Orphaned Tactics List */}
            {data.coherence_index?.orphaned_tactics && data.coherence_index.orphaned_tactics.length > 0 && (
              <div className="orphaned-tactics">
                <h5>Orphaned MECE Initiatives</h5>
                <p className="orphan-help">These initiatives are not referenced by any Strategy Map objective:</p>
                <ul>
                  {data.coherence_index.orphaned_tactics.map((ref, idx) => {
                    const mapping = data.coherence_index?.tactic_mappings?.find(m => m.tactic_ref === ref)
                    return (
                      <li key={idx}>
                        <button
                          className="btn-orphan-link"
                          onClick={() => handleNavigateToMeceTactic(ref)}
                        >
                          {ref}: {mapping?.tactic_title || 'Unknown'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            {/* Validation Warnings */}
            {data.validation_warnings && data.validation_warnings.length > 0 && (
              <div className="validation-warnings">
                <h5>Validation Warnings</h5>
                <ul>
                  {data.validation_warnings.map((warning, idx) => (
                    <li key={idx} className="warning-item">{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <StrategyMapRenderer
          data={data}
          onNodeClick={onNodeClick}
          setWorkspaceSubTab={setWorkspaceSubTab}
          executionHealth={executionHealth}
        />

        {/* Report sync status toast */}
        {syncStatus === 'syncing' && (
          <div className="sync-toast syncing">Syncing report...</div>
        )}
        {syncStatus === 'synced' && (
          <div className="sync-toast synced">Report updated</div>
        )}
        {syncStatus === 'failed' && (
          <div className="sync-toast failed">Report sync failed (changes saved)</div>
        )}
      </div>
    )
  }

  return (
    <div className="strategy-map-editor-simple edit-mode">
      <div className="editor-toolbar">
        <div className="toolbar-status">
          {isDirty && <span className="status-badge">Unsaved Changes</span>}
          {isSaving && <span className="status-badge saving">Saving...</span>}
        </div>
        <div className="toolbar-actions">
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={isSaving || !isDirty}
          >
            Save Changes
          </button>
          <button
            className="btn-cancel"
            onClick={handleEditToggle}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <strong>⚠️ Validation Errors:</strong>
          <ul>
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="perspectives-editor">
        {draftPerspectives.map(perspective => (
          <div key={perspective.perspective_id} className="perspective-section">
            <div className="perspective-header">
              <h5 className="perspective-name">{perspective.perspective_name}</h5>
              <button
                className="btn-add-objective"
                onClick={() => handleAddObjective(perspective.perspective_id)}
              >
                + Add Objective
              </button>
            </div>

            <div className="objectives-list">
              {perspective.objectives.length === 0 ? (
                <div className="empty-objectives">
                  No objectives. Click "+ Add Objective" to add one.
                </div>
              ) : (
                perspective.objectives.map(objective => (
                  <div key={objective.objective_id} className="objective-editor">
                    <div className="objective-field">
                      <label>Objective Name</label>
                      <input
                        type="text"
                        value={objective.objective_name}
                        onChange={(e) => handleUpdateObjective(
                          perspective.perspective_id,
                          objective.objective_id,
                          'objective_name',
                          e.target.value
                        )}
                        placeholder="Enter objective name"
                      />
                    </div>
                    <div className="objective-field">
                      <label>Description</label>
                      <textarea
                        value={objective.description}
                        onChange={(e) => handleUpdateObjective(
                          perspective.perspective_id,
                          objective.objective_id,
                          'description',
                          e.target.value
                        )}
                        placeholder="Describe this objective"
                        rows={3}
                      />
                    </div>
                    <button
                      className="btn-delete-objective"
                      onClick={() => handleDeleteObjective(perspective.perspective_id, objective.objective_id)}
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {isEditMode && (
        <section className="causal-links-editor">
          <h4>Causal Links</h4>
          <p className="causal-links-help">
            Define directional cause-and-effect relationships between strategic objectives. Typical Kaplan-Norton flow: Learning &amp; Growth → Internal Process → Customer → Financial.
          </p>
          {draftCausalLinks.length === 0 && (
            <p className="causal-links-empty">No causal links yet.</p>
          )}
          {draftCausalLinks.map((link, idx) => (
            <div
              key={idx}
              className="causal-link-row"
              data-testid={`causal-link-row-${idx}`}
            >
              <select
                data-testid={`causal-link-source-${idx}`}
                value={link.source_objective_id}
                onChange={e => handleUpdateCausalLink(idx, 'source_objective_id', e.target.value)}
                disabled={isSampleCompany}
              >
                <option value="">— source —</option>
                {draftPerspectives.flatMap(p =>
                  p.objectives.map(o => (
                    <option key={o.objective_id} value={o.objective_id}>
                      {p.perspective_name} / {o.objective_name}
                    </option>
                  ))
                )}
              </select>
              <span className="causal-link-arrow">→</span>
              <select
                data-testid={`causal-link-target-${idx}`}
                value={link.target_objective_id}
                onChange={e => handleUpdateCausalLink(idx, 'target_objective_id', e.target.value)}
                disabled={isSampleCompany}
              >
                <option value="">— target —</option>
                {draftPerspectives.flatMap(p =>
                  p.objectives.map(o => (
                    <option key={o.objective_id} value={o.objective_id}>
                      {p.perspective_name} / {o.objective_name}
                    </option>
                  ))
                )}
              </select>
              <input
                data-testid={`causal-link-relationship-${idx}`}
                className="causal-link-relationship"
                value={link.relationship}
                placeholder="relationship (e.g. enables, drives)"
                onChange={e => handleUpdateCausalLink(idx, 'relationship', e.target.value)}
                disabled={isSampleCompany}
              />
              <button
                data-testid={`causal-link-delete-${idx}`}
                className="causal-link-delete-btn"
                onClick={() => handleDeleteCausalLink(idx)}
                disabled={isSampleCompany}
                title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Delete this causal link'}
                aria-label={`Delete causal link ${idx + 1}`}
              >🗑</button>
            </div>
          ))}
          <button
            data-testid="add-causal-link"
            className="add-causal-link-btn"
            onClick={handleAddCausalLink}
            disabled={isSampleCompany}
            title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Add a new causal link'}
          >+ Add Causal Link</button>
        </section>
      )}
    </div>
  )
}
